import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const API = '/api';

function SectionHeader({ num, title }) {
  return (
    <div className="sec-divider">
      <span className="sec-num">{num}</span>
      <div className="sec-line" />
      <span className="sec-title">{title}</span>
      <div className="sec-mark" />
    </div>
  );
}

function FieldRow({ label, children, hint, testId }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <label style={{
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: "0.58rem",
        letterSpacing: "0.18em",
        color: "#888",
        display: "block",
        marginBottom: 6,
        textTransform: "uppercase",
      }}>
        {label}
      </label>
      {children}
      {hint && (
        <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.55rem", color: "#bbb", marginTop: 5, letterSpacing: "0.08em" }}>
          {hint}
        </p>
      )}
    </div>
  );
}

export default function SettingsPage() {
  const [settings, setSettings] = useState({ notion_api_key: "", notion_database_id: "", polling_interval_minutes: 5040 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [touched, setTouched] = useState({ key: false });
  const [pollingHours, setPollingHours] = useState(84); // Default 3.5 days
  const [notionDbsText, setNotionDbsText] = useState("");

  // Obsidian state
  const [obsidian, setObsidian] = useState({ api_url: "", api_key: "", vault_folder: "", default_tags: "" });
  const [obsidianConfigured, setObsidianConfigured] = useState(false);
  const [showObsidianKey, setShowObsidianKey] = useState(false);
  const [savingObsidian, setSavingObsidian] = useState(false);
  const [touchedObsidian, setTouchedObsidian] = useState({ key: false });

  // Supadata state (cloud transcript fallback)
  const [supadata, setSupadata] = useState({ api_key: "" });
  const [supadataConfigured, setSupadataConfigured] = useState(false);
  const [showSupadataKey, setShowSupadataKey] = useState(false);
  const [savingSupadata, setSavingSupadata] = useState(false);
  const [touchedSupadata, setTouchedSupadata] = useState({ key: false });

  useEffect(() => {
    axios.get(`${API}/settings`).then(res => {
      setSettings({
        notion_api_key: res.data.notion_api_key || "",
        notion_database_id: res.data.notion_database_id || "",
        polling_interval_minutes: res.data.polling_interval_minutes || 5040
      });
      setPollingHours(Math.round((res.data.polling_interval_minutes || 5040) / 60));
      setConfigured(res.data.has_notion || false);
        setNotionDbsText((res.data.notion_databases || []).map(d => d.category + " = " + d.database_id).join("\n"));
      // Obsidian
      setObsidian({
        api_url: res.data.obsidian_api_url || "",
        api_key: res.data.obsidian_api_key || "",
        vault_folder: res.data.obsidian_vault_folder || "",
        default_tags: (res.data.obsidian_default_tags || []).join(", "),
      });
      setObsidianConfigured(res.data.has_obsidian || false);
      // Supadata
      setSupadata({ api_key: res.data.supadata_api_key || "" });
      setSupadataConfigured(res.data.has_supadata || false);
    }).catch(() => toast.error("FAILED TO LOAD SETTINGS"))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        polling_interval_minutes: Math.max(1, pollingHours * 60)
      };
      if (touched.key && settings.notion_api_key && !settings.notion_api_key.startsWith("***")) {
        payload.notion_api_key = settings.notion_api_key;
      }
      if (settings.notion_database_id) payload.notion_database_id = settings.notion_database_id;
      const _parsed = notionDbsText.split("\n").map(line => { const i = line.indexOf("="); if (i < 0) return null; const c = line.slice(0, i).trim(); const d = line.slice(i + 1).trim(); return c && d ? { category: c, database_id: d } : null; }).filter(Boolean);
      payload.notion_databases = _parsed;
      
      await axios.put(`${API}/settings`, payload);
      toast.success("SETTINGS SAVED");
      setConfigured(true);
      setTouched({ key: false });
      const res = await axios.get(`${API}/settings`);
      setSettings({ 
        notion_api_key: res.data.notion_api_key || "", 
        notion_database_id: res.data.notion_database_id || "",
        polling_interval_minutes: res.data.polling_interval_minutes || 5040
      });
      setPollingHours(Math.round((res.data.polling_interval_minutes || 5040) / 60));
    } catch (e) {
      toast.error(e.response?.data?.detail || "SAVE FAILED");
    } finally { setSaving(false); }
  };

  const handleSaveObsidian = async (e) => {
    e.preventDefault();
    setSavingObsidian(true);
    try {
      const payload = {};
      if (obsidian.api_url) payload.obsidian_api_url = obsidian.api_url;
      if (touchedObsidian.key && obsidian.api_key && !obsidian.api_key.startsWith("***")) {
        payload.obsidian_api_key = obsidian.api_key;
      }
      if (obsidian.vault_folder) payload.obsidian_vault_folder = obsidian.vault_folder;
      const tags = obsidian.default_tags.split(",").map(t => t.trim()).filter(Boolean);
      if (tags.length) payload.obsidian_default_tags = tags;

      await axios.put(`${API}/settings`, payload);
      toast.success("OBSIDIAN SETTINGS SAVED");
      setObsidianConfigured(true);
      setTouchedObsidian({ key: false });
      const res = await axios.get(`${API}/settings`);
      setObsidian({
        api_url: res.data.obsidian_api_url || "",
        api_key: res.data.obsidian_api_key || "",
        vault_folder: res.data.obsidian_vault_folder || "",
        default_tags: (res.data.obsidian_default_tags || []).join(", "),
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "OBSIDIAN SAVE FAILED");
    } finally { setSavingObsidian(false); }
  };

  const handleSaveSupadata = async (e) => {
    e.preventDefault();
    setSavingSupadata(true);
    try {
      const payload = {};
      if (touchedSupadata.key && supadata.api_key && !supadata.api_key.startsWith("***")) {
        payload.supadata_api_key = supadata.api_key;
      }
      if (Object.keys(payload).length === 0) {
        toast.error("NO CHANGES TO SAVE");
        setSavingSupadata(false);
        return;
      }
      await axios.put(`${API}/settings`, payload);
      toast.success("SUPADATA KEY SAVED");
      setSupadataConfigured(true);
      setTouchedSupadata({ key: false });
      const res = await axios.get(`${API}/settings`);
      setSupadata({ api_key: res.data.supadata_api_key || "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "SUPADATA SAVE FAILED");
    } finally { setSavingSupadata(false); }
  };

  if (loading) return (
    <div style={{ maxWidth: 640 }}>
      <div style={{ height: 32, background: "#eee", width: 240, marginBottom: 32 }} />
      <div style={{ border: "2px solid #111", background: "#fff", padding: 32, opacity: 0.4 }}>
        {[1,2,3].map(i => <div key={i} style={{ height: 14, background: "#eee", marginBottom: 16 }} />)}
      </div>
    </div>
  );

  return (
    <div className="anim-in r-full" style={{ maxWidth: 700 }} data-testid="settings-page">
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.6rem", letterSpacing: "0.2em", color: "#CC0000", marginBottom: 6 }}>
          SYSTEM / CONFIGURATION
        </div>
        <h1 style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "2rem", fontWeight: 700, color: "#111", margin: 0, lineHeight: 1 }}>SETTINGS</h1>
        <div style={{ height: 3, background: "#111", marginTop: 10, width: 64 }} />
      </div>

      {/* Status banner */}
      {configured && (
        <div className="anim-in" style={{ marginBottom: 20, background: "#fff", border: "2px solid #111", padding: "10px 16px", display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 6, height: 6, background: "#111" }} />
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.62rem", letterSpacing: "0.12em", color: "#111" }}>
            NOTION INTEGRATION ACTIVE
          </span>
        </div>
      )}

      {/* Settings form */}
      <div style={{ marginBottom: 24 }}>
        <SectionHeader num="01" title="CONFIGURATION" />
        <div style={{ background: "#fff", border: "2px solid #111", padding: "28px", position: "relative" }}>
          <div style={{ position: "absolute", top: -3, left: -3, width: 6, height: 6, background: "#CC0000" }} />
          <div style={{ position: "absolute", top: -3, right: -3, width: 6, height: 6, background: "#CC0000" }} />
          <form onSubmit={handleSave}>
            <FieldRow label="NOTION API KEY (INTERNAL INTEGRATION SECRET)">
              <div style={{ position: "relative" }}>
                <input
                  data-testid="notion-api-key-input"
                  type={showKey ? "text" : "password"}
                  value={settings.notion_api_key}
                  onChange={e => { setSettings(p => ({ ...p, notion_api_key: e.target.value })); setTouched(p => ({ ...p, key: true })); }}
                  placeholder="SECRET_..."
                  className="ind-input"
                  style={{ paddingRight: 40 }}
                />
                <button
                  type="button"
                  onClick={() => setShowKey(!showKey)}
                  data-testid="toggle-key-visibility"
                  style={{
                    position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
                    background: "none", border: "none", cursor: "pointer",
                    fontFamily: "'JetBrains Mono', monospace", fontSize: "0.6rem", color: "#888",
                  }}
                >
                  {showKey ? "HIDE" : "SHOW"}
                </button>
              </div>
            </FieldRow>

            <FieldRow
              label="NOTION DATABASE ID"
              hint="EXTRACTED FROM DATABASE URL: notion.so/workspace/[DATABASE-ID]?v=..."
            >
              <input
                data-testid="notion-database-id-input"
                type="text"
                value={settings.notion_database_id}
                onChange={e => setSettings(p => ({ ...p, notion_database_id: e.target.value }))}
                placeholder="XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX"
                className="ind-input"
              />
            </FieldRow>

            <div style={{ borderTop: "1px solid #e8e8e4", margin: "24px 0", paddingTop: 24 }}>
               <FieldRow 
                 label="POLLING FREQUENCY (HOURS)"
                 hint={`CURRENTLY: EVERY ${pollingHours} HOURS (~${(pollingHours/24).toFixed(1)} DAYS)`}
               >
                 <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                   <input
                     data-testid="polling-interval-input"
                     type="number"
                     min="1"
                     value={pollingHours}
                     onChange={e => setPollingHours(parseInt(e.target.value) || 1)}
                     className="ind-input"
                     style={{ width: 100 }}
                   />
                   <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.6rem", color: "#555" }}>HOURS</span>
                 </div>
               </FieldRow>
            </div>

            <FieldRow
                  label="NOTION DATABASES (CATEGORY = DATABASE ID)"
                  hint="ONE PER LINE, e.g. Finance = 30fe9595977b... EACH DATABASE MUST BE SHARED WITH YOUR NOTION INTEGRATION"
                >
                  <textarea
                    data-testid="notion-databases-input"
                    value={notionDbsText}
                    onChange={e => setNotionDbsText(e.target.value)}
                    placeholder="Finance = 30fe95...    Tech = abc123..."
                    className="ind-input"
                    style={{ minHeight: 90, resize: "vertical", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.7rem", lineHeight: 1.6 }}
                  />
                </FieldRow>

                <button
              data-testid="save-settings-btn"
              type="submit"
              disabled={saving}
              className="ind-btn-primary"
            >
              {saving ? <Loader2 size={13} className="animate-spin" /> : null}
              SAVE CONFIGURATION
            </button>
          </form>
        </div>
      </div>

      {/* Setup guide */}
      <div style={{ marginBottom: 24 }} data-testid="setup-guide">
        <SectionHeader num="02" title="SETUP PROCEDURE" />
        <div style={{ border: "2px solid #111", background: "#fff" }}>
          {[
            { step: "01", text: "GO TO notion.so/my-integrations → NEW INTEGRATION", link: { href: "https://www.notion.so/my-integrations", label: "↗ OPEN" } },
            { step: "02", text: "NAME YOUR INTEGRATION AND COPY THE INTERNAL INTEGRATION SECRET" },
            { step: "03", text: 'CREATE A DATABASE IN NOTION WITH A "NAME" TITLE PROPERTY' },
            { step: "04", text: "SHARE THE DATABASE WITH YOUR INTEGRATION VIA THE ··· MENU" },
            { step: "05", text: "COPY DATABASE ID FROM URL AND PASTE ABOVE WITH YOUR KEY" },
          ].map((item, i) => (
            <div
              key={item.step}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 0,
                borderBottom: i < 4 ? "1px solid #e8e8e4" : "none",
              }}
            >
              <div style={{
                padding: "14px 16px",
                borderRight: "1px solid #e8e8e4",
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: "0.6rem",
                fontWeight: 700,
                color: "#CC0000",
                flexShrink: 0,
                minWidth: 48,
              }}>
                {item.step}
              </div>
              <div style={{ padding: "14px 16px", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.62rem", color: "#555", letterSpacing: "0.08em", lineHeight: 1.5, flex: 1 }}>
                {item.text}
              </div>
              {item.link && (
                <div style={{ padding: "14px 16px", flexShrink: 0 }}>
                  <a
                    href={item.link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="notion-integrations-link"
                    style={{
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: "0.58rem",
                      color: "#CC0000",
                      textDecoration: "none",
                      letterSpacing: "0.1em",
                    }}
                  >
                    {item.link.label}
                  </a>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Supadata API — cloud transcript fallback */}
      <div style={{ marginBottom: 24 }}>
        <SectionHeader num="03" title="TRANSCRIPT ENGINE" />

        {supadataConfigured && (
          <div className="anim-in" style={{ marginBottom: 12, background: "#fff", border: "2px solid #111", padding: "10px 16px", display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 6, height: 6, background: "#059669" }} />
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.62rem", letterSpacing: "0.12em", color: "#111" }}>
              SUPADATA CLOUD FALLBACK ACTIVE
            </span>
          </div>
        )}

        <div style={{ background: "#fff", border: "2px solid #111", padding: "28px", position: "relative" }}>
          <div style={{ position: "absolute", top: -3, left: -3, width: 6, height: 6, background: "#059669" }} />
          <div style={{ position: "absolute", top: -3, right: -3, width: 6, height: 6, background: "#059669" }} />

          <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.58rem", color: "#666", lineHeight: 1.7, marginTop: 0, marginBottom: 20 }}>
            YouTube blocks transcript requests from cloud servers. Supadata provides a free API (100 credits/month) that bypasses this restriction.
            Sign up at <a href="https://supadata.ai" target="_blank" rel="noopener noreferrer" style={{ color: "#059669", textDecoration: "none", fontWeight: 700 }}>supadata.ai</a> — no credit card required.
          </p>

          <form onSubmit={handleSaveSupadata}>
            <FieldRow label="SUPADATA API KEY">
              <div style={{ position: "relative" }}>
                <input
                  type={showSupadataKey ? "text" : "password"}
                  value={supadata.api_key}
                  onChange={e => { setSupadata(p => ({ ...p, api_key: e.target.value })); setTouchedSupadata(p => ({ ...p, key: true })); }}
                  placeholder="sd_..."
                  className="ind-input"
                  style={{ paddingRight: 40 }}
                />
                <button
                  type="button"
                  onClick={() => setShowSupadataKey(p => !p)}
                  style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.55rem", color: "#059669", letterSpacing: "0.1em" }}
                >
                  {showSupadataKey ? "HIDE" : "SHOW"}
                </button>
              </div>
            </FieldRow>

            <button type="submit" disabled={savingSupadata} className="ind-btn-primary" style={{ width: "100%", justifyContent: "center" }}>
              {savingSupadata ? <Loader2 size={14} className="animate-spin" /> : "SAVE TRANSCRIPT KEY"}
            </button>
          </form>
        </div>
      </div>

      {/* System info */}
      <div>
        <SectionHeader num="04" title="SYSTEM INFO" />
        <div style={{ border: "1px solid #e8e8e4", background: "#fff", padding: "16px 20px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, flexWrap: "wrap" }} className="r-grid-1">
          {[
            { k: "AI ENGINE", v: "GEMINI 3 FLASH" },
            { k: "TRANSCRIPT", v: supadataConfigured ? "YT-API + SUPADATA" : "YT-TRANSCRIPT-API" },
            { k: "DATABASE", v: "MONGODB" },
            { k: "VERSION", v: "1.0.0" },
          ].map(({ k, v }) => (
            <div key={k}>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.52rem", color: "#bbb", letterSpacing: "0.14em", marginBottom: 3 }}>{k}</div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.65rem", color: "#111", fontWeight: 600 }}>{v}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
